import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { allMonths, euro } from '@fleet/shared';
import { Badge, Button } from '../../design-system';
import { ConfirmModal } from '../../components/ui/ConfirmModal';
import { AutoLink, PasLink } from '../../components/ui/EntiteitLink';
import { Eyebrow } from '../../components/ui/Eyebrow';
import { PencilIcon } from '../../components/ui/Icons';
import { Panel } from '../../components/ui/Panel';
import { PeriodeKiezer } from '../../components/ui/PeriodeKiezer';
import { SectieKop } from '../../components/ui/SectieKop';
import { Timeline } from '../../components/ui/Timeline';
import { ApiError } from '../../lib/apiClient';
import { EditPersoonModal } from './EditPersoonModal';
import { KoppelAutoModal, KoppelPasModal } from './KoppelModals';
import { KoppelHistorie } from './KoppelHistorie';
import { persoonTijdlijn, tijdlijnMaanden } from './persoonTijdlijn';
import { VariabeleKosten } from './VariabeleKosten';
import {
  useArchiveerPersoon,
  useDearchiveerPersoon,
  usePersoonDetail,
  useUnlinkAuto,
  useUnlinkPas,
} from './personen.api';

type UnlinkTarget = { soort: 'auto' | 'pas'; id: string; label: string };

export function PersoonDetailPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const { data, isLoading, isError } = usePersoonDetail(id);

  const unlinkAuto = useUnlinkAuto(id);
  const unlinkPas = useUnlinkPas(id);
  const archiveer = useArchiveerPersoon(id);
  const dearchiveer = useDearchiveerPersoon(id);

  const [showEdit, setShowEdit] = useState(false);
  const [showKoppelAuto, setShowKoppelAuto] = useState(false);
  const [showKoppelPas, setShowKoppelPas] = useState(false);
  const [unlink, setUnlink] = useState<UnlinkTarget | null>(null);
  const [unlinkTot, setUnlinkTot] = useState('');
  const [unlinkErr, setUnlinkErr] = useState('');
  /** Unlinking a car: also hand in the fuel card(s)? See `openUnlinkAuto`. */
  const [ookPas, setOokPas] = useState(false);
  const [showArchive, setShowArchive] = useState(false);
  /** Periode van het kostenblok en van de tijdlijn, elk in hun eigen sectiekop. */
  const [kostenFilter, setKostenFilter] = useState<string | null>('YTD');
  const [tijdlijnPeriode, setTijdlijnPeriode] = useState('YTD');
  const [archiveErr, setArchiveErr] = useState('');

  if (isError) return <div style={{ color: '#8A4029' }}>Persoon niet gevonden.</div>;
  if (isLoading || !data) return <div style={{ color: '#888888' }}>Laden…</div>;

  const { persoon, autos, passen, koppelingen, costLines, tijdlijn } = data;
  const tijdlijnMaandenLijst = tijdlijnMaanden(tijdlijn, costLines);
  const gebeurtenissen = persoonTijdlijn(tijdlijn, costLines, tijdlijnPeriode);
  const kostenMaanden = allMonths(costLines);

  /**
   * Open the unlink dialog for a car, and decide up front whether handing in the
   * fuel card should be pre-ticked.
   *
   * Ticked when this is the person's only car, because then the card has nothing
   * left to belong to and the two almost always go together. With a second car
   * still linked the card probably stays with that one, so it is offered but not
   * assumed — silently unlinking it there would break cost attribution on a car
   * that is still in use.
   */
  function openUnlinkAuto(kenteken: string, label: string) {
    setUnlinkErr('');
    setUnlinkTot('');
    setOokPas(passen.length > 0 && autos.length === 1);
    setUnlink({ soort: 'auto', id: kenteken, label });
  }

  async function doUnlink() {
    if (!unlink) return;
    setUnlinkErr('');
    const tot = unlinkTot || undefined;
    try {
      if (unlink.soort === 'auto') {
        await unlinkAuto.mutateAsync({ kenteken: unlink.id, tot });
        // Na de auto, met dezelfde einddatum, zodat de kostentoerekening van beide
        // op hetzelfde moment stopt. Eén voor één: `hersyncKoppeling` draait per
        // mutatie en herverdeelt daarbij de kilometerstanden.
        if (ookPas) {
          for (const p of passen) await unlinkPas.mutateAsync({ cardId: p.id, tot });
        }
      } else {
        await unlinkPas.mutateAsync({ cardId: unlink.id, tot });
      }
      setUnlink(null);
      setUnlinkTot('');
    } catch (e) {
      const melding = e instanceof ApiError ? e.message : 'Loskoppelen mislukt';
      // De auto kan al los zijn terwijl de pas faalde; dat moet je weten, anders
      // denk je dat er niets is gebeurd en probeer je het opnieuw.
      setUnlinkErr(
        unlink.soort === 'auto' && ookPas
          ? `${melding} — controleer of de auto en de pas allebei zijn losgekoppeld.`
          : melding,
      );
    }
  }

  async function doArchive() {
    setArchiveErr('');
    try {
      await archiveer.mutateAsync();
      setShowArchive(false);
      navigate('/personen/archief');
    } catch (e) {
      setArchiveErr(e instanceof ApiError ? e.message : 'Archiveren mislukt');
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 16,
          flexWrap: 'wrap',
        }}
      >
        <div>
          <Button variant="ghost" size="sm" onClick={() => navigate('/personen')}>
            ← Alle personen
          </Button>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 8 }}>
            <h2 style={{ margin: 0, fontSize: 26, fontWeight: 900, color: '#111111' }}>
              {persoon.naam}
            </h2>
            {persoon.actief ? (
              <Badge tone="success">Actief</Badge>
            ) : (
              <Badge tone="neutral">Gearchiveerd</Badge>
            )}
          </div>
          <div style={{ fontSize: 13, color: '#666666', marginTop: 4 }}>
            {persoon.email || 'geen e-mail'}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Button
            variant="secondary"
            size="sm"
            iconOnly
            title="Bewerken"
            aria-label="Persoon bewerken"
            onClick={() => setShowEdit(true)}
          >
            <PencilIcon />
          </Button>
          {persoon.actief ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setArchiveErr('');
                setShowArchive(true);
              }}
            >
              Archiveren
            </Button>
          ) : (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => dearchiveer.mutate()}
              disabled={dearchiveer.isPending}
            >
              Dearchiveren
            </Button>
          )}
        </div>
      </div>

      <SectieKop titel="Koppeling" />

      {/* Auto's & Passen */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
        <Panel style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <Eyebrow>Auto&apos;s</Eyebrow>
          {autos.length === 0 && (
            <div style={{ fontSize: 13, color: '#888888' }}>Geen auto gekoppeld.</div>
          )}
          {autos.map((a) => (
            <div
              key={a.kenteken}
              style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13 }}
            >
              <AutoLink kenteken={a.kenteken} />
              <span style={{ color: '#666666', flex: 1 }}>
                {a.merk} {a.model}
              </span>
              {a.eigendom === 'LEEN' && <Badge tone="warning">Leen</Badge>}
              {a.eigendom === 'LEASE' && <Badge tone="blue">Lease</Badge>}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => openUnlinkAuto(a.kenteken, `${a.kenteken} · ${a.merk} ${a.model}`)}
              >
                Loskoppelen
              </Button>
            </div>
          ))}
          {persoon.actief ? (
            <Button
              variant="secondary"
              size="sm"
              style={{ alignSelf: 'flex-start' }}
              onClick={() => setShowKoppelAuto(true)}
            >
              + Auto koppelen
            </Button>
          ) : (
            <div style={{ fontSize: 12, color: '#888888' }}>
              Persoon is uit dienst — koppelen is uitgeschakeld.
            </div>
          )}
        </Panel>

        <Panel style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <Eyebrow>Passen</Eyebrow>
          {passen.length === 0 && (
            <div style={{ fontSize: 13, color: '#888888' }}>Geen pas gekoppeld.</div>
          )}
          {passen.map((p) => (
            <div
              key={p.id}
              style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13 }}
            >
              <PasLink pasId={p.id} pasnummer={p.pasnummer} style={{ fontSize: 12 }} />
              {p.fictief && <Badge tone="warning">Fictief</Badge>}
              <span style={{ flex: 1, textAlign: 'right', fontWeight: 700 }}>
                {euro(p.kostenTotaal)}
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setUnlinkErr('');
                  setUnlinkTot('');
                  setUnlink({ soort: 'pas', id: p.id, label: `Pas ${p.pasnummer}` });
                }}
              >
                Loskoppelen
              </Button>
            </div>
          ))}
          {persoon.actief ? (
            <Button
              variant="secondary"
              size="sm"
              style={{ alignSelf: 'flex-start' }}
              onClick={() => setShowKoppelPas(true)}
            >
              + Pas koppelen
            </Button>
          ) : (
            <div style={{ fontSize: 12, color: '#888888' }}>
              Persoon is uit dienst — koppelen is uitgeschakeld.
            </div>
          )}
        </Panel>
      </div>

      <KoppelHistorie koppelingen={koppelingen} />

      <SectieKop titel="Kosten persoon">
        <PeriodeKiezer
          months={kostenMaanden}
          period={kostenFilter ?? 'ALL'}
          onPeriod={(p) => setKostenFilter(p === 'ALL' ? null : p)}
        />
      </SectieKop>
      <VariabeleKosten costLines={costLines} filter={kostenFilter} onFilter={setKostenFilter} />

      {/* Koppelmomenten en transacties door elkaar, zodat je ziet dat iemand een
          pas kreeg en drie dagen later voor het eerst tankte. Scrollend in een
          vast venster, net als op de autopagina: een jaar tanken is honderden
          regels en zou de pagina anders eindeloos maken. */}
      <SectieKop titel="Tijdlijn">
        <PeriodeKiezer
          months={tijdlijnMaandenLijst}
          period={tijdlijnPeriode}
          onPeriod={(p) => setTijdlijnPeriode(p ?? 'ALL')}
        />
      </SectieKop>
      <Panel style={{ padding: '22px 26px' }}>
        {gebeurtenissen.length === 0 ? (
          <div style={{ fontSize: 13, color: '#888888' }}>Niets gebeurd in deze periode.</div>
        ) : (
          <div style={{ maxHeight: 420, overflowY: 'auto', paddingRight: 4 }}>
            <Timeline events={gebeurtenissen} />
          </div>
        )}
      </Panel>

      {/* Modals */}
      {showEdit && <EditPersoonModal persoon={persoon} onClose={() => setShowEdit(false)} />}
      {showKoppelAuto && (
        <KoppelAutoModal
          persoonId={id}
          reedsGekoppeld={autos.map((a) => a.kenteken)}
          onClose={() => setShowKoppelAuto(false)}
        />
      )}
      {showKoppelPas && <KoppelPasModal persoonId={id} onClose={() => setShowKoppelPas(false)} />}
      {unlink && (
        <ConfirmModal
          title="Loskoppelen"
          message={`${unlink.label} loskoppelen van ${persoon.naam}?`}
          confirmLabel={
            unlink.soort === 'auto' && ookPas
              ? passen.length === 1
                ? 'Auto en pas loskoppelen'
                : 'Auto en passen loskoppelen'
              : 'Loskoppelen'
          }
          busy={unlinkAuto.isPending || unlinkPas.isPending}
          error={unlinkErr}
          extra={
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <label
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 4,
                  fontSize: 12,
                  color: '#888888',
                }}
              >
                Einddatum (optioneel, leeg = vandaag)
                <input
                  type="date"
                  value={unlinkTot}
                  onChange={(e) => setUnlinkTot(e.target.value)}
                  style={{
                    fontFamily: 'inherit',
                    fontSize: 14,
                    padding: '8px 10px',
                    border: '1px solid #C9BCA9',
                    borderRadius: 4,
                    background: '#FBF5EE',
                  }}
                />
              </label>

              {/* Auto en pas gaan bijna altijd samen de deur uit, dus dat hoort één
                  handeling te zijn in plaats van twee die je kunt vergeten. */}
              {unlink.soort === 'auto' && passen.length > 0 && (
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 6,
                    background: '#FBF5EE',
                    border: '1px solid #E3D9CC',
                    borderRadius: 4,
                    padding: '10px 12px',
                  }}
                >
                  <label
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      fontSize: 13,
                      cursor: 'pointer',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={ookPas}
                      onChange={(e) => setOokPas(e.target.checked)}
                    />
                    <span>
                      Ook {passen.length === 1 ? 'de tankpas' : `de ${passen.length} tankpassen`}{' '}
                      loskoppelen
                    </span>
                  </label>
                  <span
                    style={{ fontSize: 12, color: '#888888', lineHeight: 1.5, paddingLeft: 24 }}
                  >
                    <span style={{ fontFamily: "'Roboto Mono', monospace" }}>
                      {passen.map((p) => p.pasnummer).join(', ')}
                    </span>
                    {autos.length > 1 && (
                      <>
                        {' '}
                        — let op: {persoon.naam} houdt nog{' '}
                        {autos.length === 2
                          ? 'een andere auto'
                          : `${autos.length - 1} andere auto's`}
                        , dus de pas hoort daar misschien bij.
                      </>
                    )}
                  </span>
                </div>
              )}
            </div>
          }
          onConfirm={doUnlink}
          onClose={() => setUnlink(null)}
        />
      )}
      {showArchive && (
        <ConfirmModal
          title="Persoon archiveren"
          message={`Weet je zeker dat je ${persoon.naam} wilt archiveren (uit dienst)? Dit kan alleen als er geen auto's of passen meer gekoppeld zijn.`}
          confirmLabel="Archiveren"
          danger
          busy={archiveer.isPending}
          error={archiveErr}
          onConfirm={doArchive}
          onClose={() => setShowArchive(false)}
        />
      )}
    </div>
  );
}
